import Section from "../Section";
import MobileMenu from '../global/MobileMenu';
import Menu from '../global/Menu';
import { CART_UPDATE_EVENT } from '../ThemeBase';

export default class Header extends Section {
  /**
   * @param {import('../ThemeBase').default} theme
   * @param {HTMLElement} element
   * @param {boolean} pageLoad
   */
  constructor(theme, element, pageLoad) {
    super(theme, element, pageLoad);

    this.element = element;

    // Initialize scroll tracking for sticky header behavior
    this.lastScrollTop = 0;

    // Bind event handlers to maintain context
    this.handleCartButtonClick = this.onCartButtonClick.bind(this);
    this.handleOffersLinkClick = this.onOffersLinkClick.bind(this);
    this.handleMobileMenuButtonClick = this.onMobileMenuButtonClick.bind(this);
    this.handleNavDrawerOverlayClick = this.onNavDrawerOverlayClick.bind(this);
    this.handleCartUpdate = this.onCartUpdate.bind(this);

    // Initialize mobile menu component
    this.mobileMenu = new MobileMenu(theme, this.element.querySelector('.drawer--left'));

    // Set up cart drawer button listeners
    Array.prototype.forEach.call(
      this.element.querySelectorAll('.js-drawer-open-right-link'),
      (el) => el.addEventListener('click', this.handleCartButtonClick),
    );

    // Set up mobile menu button listener
    this.element.querySelector('.js-drawer-open-left-link').addEventListener('click', this.handleMobileMenuButtonClick);

    // Set up offers drawer if available
    const offersLinks = this.element.querySelectorAll('.js-drawer-open-offers-link');
    if (offersLinks.length > 0) {
      offersLinks.forEach(el => el.addEventListener('click', this.handleOffersLinkClick));

      // If the icon setting is changed, need to inject the new icon in the drawer
      if (window.Shopify.designMode) {
        window.eventBus.emit('injectIcon:offers:drawer');
      }
    } else if (window.Shopify.designMode) {
      // If no offers icon and the drawer is open in the editor, close it
      window.eventBus.emit('close:offers:drawer');
    }

    // Listen for cart updates globally
    document.documentElement.addEventListener(CART_UPDATE_EVENT, this.handleCartUpdate);

    // Close mobile menu on overlay click
    this.element.querySelector('#NavDrawerOverlay').addEventListener('click', this.handleNavDrawerOverlayClick);

    // Initialize header variables and behaviors
    this.handleStickyHeaderVars();

    // Load accessible navigation menu
    this.loadAccessibleMenu();

    // Load appropriate header behavior based on sticky setting
    if (this.element.dataset.headerSticky === 'true' && (this.element.dataset.inspectorActive == 'false' || !this.element.dataset.inspectorActive)) {
      this.loadStickyHeader();
    } else {
      this.loadHeaderObserver();
    }

    // Initialize search drawer functionality
    this.loadSearchDrawer();

    // Initialize main menu component
    this.menu = new Menu(this, element);

    // Handle external links
    this.handleExternalLinks();

    // Initialize ResizeObserver for dynamic header height tracking
    this.initResizeObserver();

    // Fallback: Add window resize listener in case ResizeObserver is not supported
    if (!('ResizeObserver' in window)) {
      window.addEventListener('resize', this.onWindowResize);
    }
  }

  /**
   * Initialize ResizeObserver to track header height changes
   * Updates CSS variables when header dimensions change
   */
  initResizeObserver() {
    if ('ResizeObserver' in window) {
      // Create a new ResizeObserver instance
      this.resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          if (entry.target.classList.contains('mobile-nav-bar-wrapper') || entry.target.classList.contains('site-header')) {
            this.updateHeaderHeightVariables();
          }
        }
      });

      // Select the elements to observe
      const mobileNavBar = this.element.querySelector('.mobile-nav-bar-wrapper');
      const siteHeader = this.element.querySelector('.site-header');

      // Observe the elements if they exist
      if (mobileNavBar) this.resizeObserver.observe(mobileNavBar);
      if (siteHeader) this.resizeObserver.observe(siteHeader);
    }
  }

  /**
   * Load and configure accessible navigation menu
   * Sets up keyboard navigation and focus management
   */
  loadAccessibleMenu = () => {
    const siteNav = this.element.querySelector('.site-nav');
    if (siteNav) {
      siteNav.classList.add('js');
    }

    // Handle focus states for dropdowns
    this.element.querySelectorAll('.site-nav li a').forEach(anchor => {
      anchor.addEventListener('focus', function() {
        const parent = this.parentElement;
        if (parent) {
          const ul = parent.querySelector('ul');
          if (ul) {
            ul.style.display = 'block';
          }
        }
      });
    });

    this.element.querySelectorAll('.site-nav li li a').forEach(anchor => {
      anchor.removeEventListener('focus', () => {});
    });

    // Handle anchor links within mobile menu
    this.element.querySelectorAll('.mobile-nav__link').forEach(link => {
      link.addEventListener('click', (e) => {
        const a = /** @type {HTMLAnchorElement} */ (e.currentTarget);
        if (
          a.href &&
          a.href.split('#')[0] === window.location.href.split('#')[0]
        ) {
          // Anchor on same page - just close the drawer
          this.theme.toggleLeftDrawer(false);
        }
      });
    });

    this.tabindexMobileNav();
  };

  /**
   * Initialize search drawer functionality
   */
  loadSearchDrawer = () => {
    document.querySelectorAll('.search-button').forEach(item => {
      item.addEventListener('click', this.onSearch);
    });
  }

  /**
   * Load sticky header behavior with intersection observer
   * Handles header sticking and reveal-on-scroll functionality
   */
  loadStickyHeader = () => {

    if ('IntersectionObserver' in window) {
      // We need to apply sticky to child of element that is being scrolled, in
      // this case it's the whole `shopify-section`
      const headerWrapper = this.element.closest('.shopify-section.header-section');
      headerWrapper.classList.add('sticky-header');

      const revealOnScroll = this.element.dataset.headerRevealOnScroll === 'true';
  
      const observer = new IntersectionObserver(
        ([e]) => {
          if (e.intersectionRatio < 1) {
            e.target.classList.add('sticked');
            // Dispatch a custom event when the header becomes 'sticked'
            const stickedEvent = new CustomEvent('headerSticked', { detail: e.target });
            document.dispatchEvent(stickedEvent);

            if (revealOnScroll) {
              window.addEventListener('scroll', this.handleScroll);
            }
          } else {
            e.target.classList.remove('sticked');
            // Dispatch a custom event when the header becomes 'unstuck'
            const unstuckEvent = new CustomEvent('headerUnstuck', { detail: e.target });
            document.dispatchEvent(unstuckEvent);

            if (revealOnScroll) {
              window.removeEventListener('scroll', this.handleScroll);
              headerWrapper.classList.remove('sticky-header-hidden');
            }
          }
        },
        {
          threshold: [1],
        },
      );
  
      observer.observe(headerWrapper);
    }
  }

  /**
   * Load header observer for non-sticky headers
   * Tracks when header scrolls off screen
   */
  loadHeaderObserver = () => {
    if ('IntersectionObserver' in window) {
      const headerWrapper = this.element.closest('.shopify-section.header-section');
  
      const observer = new IntersectionObserver(
        ([e]) => {
          if (e.intersectionRatio > 0) {
            e.target.classList.remove('is-off-screen');
          } else {
            e.target.classList.add('is-off-screen');
          }
        },
        {
          threshold: [0],
        },
      );
  
      observer.observe(headerWrapper);
    }
  }

  /**
   * Handle navigation drawer overlay click to close mobile menu
   */
  onNavDrawerOverlayClick() {
    this.theme.toggleLeftDrawer(false);
  }

  /**
   * Handle cart update events and update item count display
   * @param {CustomEvent} e - Cart update event with cart details
   */
  onCartUpdate = (e) => {
    Array.prototype.forEach.call(
      document.querySelectorAll('.cart-item-count-header--quantity'),
      (el) => {
        el.classList.add('hide');

        const cartHasItems = e.detail.cart.item_count > 0;
        el.textContent = e.detail.cart.item_count;

        if (cartHasItems) el.classList.remove('hide');
      },
    );
  }

  /**
   * Manage tabindex for mobile navigation
   * Mobile menu should not be keyboard-navigable while viewing on desktop
   */
    tabindexMobileNav = () => {
      const navDrawerLinks = this.element.querySelectorAll('#NavDrawer a, #NavDrawer button');
      if(window.innerWidth > 767) {
        navDrawerLinks.forEach(link => link.setAttribute('tabindex', '-1'));
      }
      else {
        navDrawerLinks.forEach(link => link.removeAttribute('tabindex'));
      }
    }

  /**
   * Handle cart button click to open cart drawer
   * @param {Event} e - Click event
   */
  onCartButtonClick = (e) => {
    e.preventDefault();
    window.eventBus.emit('open:cart:drawer', { scrollToTop: false });
  }

  /**
   * Handle offers link click to open offers drawer
   * @param {Event} e - Click event
   */
  onOffersLinkClick = (e) => {
    e.preventDefault();
    window.eventBus.emit('open:offers:drawer');
  }

  /**
   * Handle mobile menu button click to toggle menu
   * @param {Event} e - Click event
   */
  onMobileMenuButtonClick = (e) => {
    e.preventDefault();
    this.mobileMenu.toggle();
  }

    /**
   * Handle window resize events
   * Updates mobile nav tabindex and header variables
   */
    onWindowResize = () => {
      this.tabindexMobileNav();
      this.handleStickyHeaderVars();
    }

  /**
   * Handle search button click to toggle search dropdown
   * @param {Event} e - Click event
   */
  onSearch = (e) => {
    e.preventDefault();
    window.eventBus.emit('toggle:search:dropdown', e);
  }

  /**
   * Handle scroll events for reveal-on-scroll sticky header
   * Shows/hides header based on scroll direction
   */
  handleScroll = () => {
    const headerWrapper = this.element.closest('.shopify-section.header-section');
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop > this.lastScrollTop && scrollTop > headerWrapper.offsetHeight) {
      // Scrolling down
      headerWrapper.classList.add('sticky-header-hidden');
    } else {
      // Scrolling up
      headerWrapper.classList.remove('sticky-header-hidden');
    }
    this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
  }

  /**
   * Handle block selection in Shopify theme editor
   * Opens mega menu when a mega menu block is selected
   * @param {Object} params - Block selection parameters
   */
  onBlockSelect = ({ element }) => {
    // Prevent menu reopening on deselect due to editor event bug where select event is fired on deselect after editing a text input setting
    if (document.body.hasAttribute('data-mega-menu-deselected')) return;

    // Open mega menu and make overlay force-hovered
    if (element.dataset.megaMenuBlockId) {
      const megaMenu = this.element.querySelector(`[data-mega-menu-block-id='${element.dataset.megaMenuBlockId}']`);
      if (megaMenu) {
        const li = megaMenu.closest('li.mega-menu');
        if (li) {
          li.classList.add('force-open');
        }
      }
      const overlayHeader = this.element.querySelector('.overlay-header');
      if (overlayHeader) {
        overlayHeader.classList.add('force-hover');
      }

    }
  }

  /**
   * Handle block deselection in Shopify theme editor
   * Closes mega menu when a block is deselected
   */
  onBlockDeselect = () => {
    // Needed to prevent menu reopening on deselect due to editor event bug where select event is fired on deselect after editing a text input setting
    document.body.setAttribute('data-mega-menu-deselected', 'true');
    setTimeout(() => {
      document.body.removeAttribute('data-mega-menu-deselected');
    }, 500);

    // Close mega menu and make overlay non-hovered
    this.element.querySelectorAll('li.mega-menu.force-open').forEach(li => li.classList.remove('force-open'));
    this.element.querySelectorAll('.overlay-header').forEach(header => header.classList.remove('force-hover'));

  }
  
  /**
   * Set a CSS variable for header height
   * @param {string} selector - Element selector to measure
   * @param {string} varName - CSS variable name to set
   */
  setStickyHeaderVar = (selector, varName) => {
    const headerHeight = this.element.querySelector(selector).getBoundingClientRect().height;
    document.documentElement.style.setProperty(varName, `${headerHeight}px`);
  }

  /**
   * Update CSS variables for sticky header heights
   * Sets both mobile and desktop header height variables
   */
    handleStickyHeaderVars = () => {
      const isStickyHeader = this.element.dataset.headerSticky === 'true';
      if (isStickyHeader) {
        this.setStickyHeaderVar('.mobile-nav-bar-wrapper', '--sticky-header-mobile-height');
        this.setStickyHeaderVar('.site-header', '--sticky-header-desktop-height');
      } else {
        this.resetStickyHeaderVar('--sticky-header-mobile-height');
        this.resetStickyHeaderVar('--sticky-header-desktop-height');
      }
  
      this.setStickyHeaderVar('.mobile-nav-bar-wrapper', '--header-height-mobile');
      this.setStickyHeaderVar('.site-header', '--header-height-desktop');
    }
  
  /**
   * Reset a CSS variable to 0px
   * @param {string} varName - CSS variable name to reset
   */
  resetStickyHeaderVar = (varName) => {
    document.documentElement.style.setProperty(varName, `0px`);
  }

  /**
   * Update header height CSS variables
   * Called by ResizeObserver when header dimensions change
   */
  updateHeaderHeightVariables() {
    this.handleStickyHeaderVars();
  }

  /**
   * Configure external links to open in new tabs
   * Adds target="_blank" and security attributes
   */
  handleExternalLinks = () => {
    const links = this.element.querySelectorAll('a');
    links.forEach(link => {
      if (link.hostname !== window.location.hostname) {
        link.target = '_blank';
        link.rel = 'noreferrer noopener';
      }
    });
  }

  /**
   * Cleanup when component is disconnected
   * Removes event listeners and disconnects observers
   */
  disconnectedCallback() {
    if (!('ResizeObserver' in window)) {
      window.removeEventListener('resize', this.onWindowResize);
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}
